import { zodResolver } from "@hookform/resolvers/zod"
import { useNavigate } from "@tanstack/react-router"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Input } from "design-system/atoms/Input"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { getErrorTranslations } from "lib/i18n"
import { AdminOnly } from "modules/shared/components/AdminOnly"
import { useCreateShopifyIntegrationConfigurationMutation } from "modules/settings/hooks/shopify/useCreateShopifyIntegrationConfigurationMutation"
import { useShopifyIntegrationQuery } from "modules/settings/hooks/shopify/useShopifyIntegrationQuery"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { UpsertShopifyIntegration } from "schema"
import { z } from "zod"
import { DeleteShopifyIntegration } from "./DeleteShopifyIntegration"

export const ShopifyAuthModal = () => {
  const { t } = useTranslation()
  const form = useForm<z.infer<typeof UpsertShopifyIntegration>>({
    resolver: zodResolver(UpsertShopifyIntegration),
  })
  const navigate = useNavigate()
  const { mutateAsync, isPending } = useCreateShopifyIntegrationConfigurationMutation()
  const { data } = useShopifyIntegrationQuery()
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

  const onSubmit = async (data: z.infer<typeof UpsertShopifyIntegration>) => {
    await mutateAsync(data)
    onOpenChange()
  }

  const onOpenChange = (isOpen: boolean = false) => {
    if (!isOpen) {
      navigate({ to: "/settings/integrations/shopify" })
    }
  }

  const { prefix, suffix } = form.watch()

  useEffect(() => {
    if (data) {
      form.reset({
        shopifyUrl: data.shopifyUrl,
        shopifyHash: data.shopifyHash,
        accessToken: data.accessToken,
        prefix: data.prefix,
        suffix: data.suffix,
      })
    }
  }, [data, form])

  return (
    <>
      <Dialog open onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("settings.integrations.shopify.authModal.title")}</DialogTitle>
            <DialogDescription>
              {t("settings.integrations.shopify.authModal.description")}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            <Form
              {...form}
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex flex-col gap-4"
              errorTranslations={getErrorTranslations()}
            >
              <div className="flex flex-col w-full gap-4">
                <FormField
                  control={form.control}
                  name="shopifyUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("settings.integrations.shopify.authModal.form.storeUrl")}
                      </FormLabel>
                      <FormControl>
                        <Input {...field} required />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="shopifyHash"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("settings.integrations.shopify.authModal.form.apiKey")}
                      </FormLabel>
                      <FormControl>
                        <Input {...field} required />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="accessToken"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("settings.integrations.shopify.authModal.form.accessToken")}
                      </FormLabel>
                      <FormControl>
                        <Input {...field} required />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="prefix"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("settings.integrations.shopify.authModal.form.prefix")}
                      </FormLabel>
                      <FormControl>
                        <Input {...field} value={prefix ?? ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="suffix"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("settings.integrations.shopify.authModal.form.suffix")}
                      </FormLabel>
                      <FormControl>
                        <Input {...field} value={suffix ?? ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <DialogFooter>
                {data?.shopifyHash ? (
                  <AdminOnly superadminOnly>
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={() => setIsDeleteDialogOpen(true)}
                    >
                      {t("settings.integrations.shopify.integrationCard.deleteIntegration.button")}
                    </Button>
                  </AdminOnly>
                ) : null}
                <Button disabled={isPending || !form.formState.isValid}>
                  {t("settings.integrations.shopify.authModal.form.submit")}
                </Button>
              </DialogFooter>
            </Form>
          </div>
        </DialogContent>
      </Dialog>
      <DeleteShopifyIntegration
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onDeleted={onOpenChange}
      />
    </>
  )
}
